import { useEffect } from "react";
import { TextPageLayout } from "@/components/TextPageLayout";
import { setMetaDescription, setCanonicalUrl, setOpenGraphTags, setTwitterCardTags } from "@/lib/seo";
import { generateWebPageSchema, generateArticleSchema, addSchemaToPage } from "@/lib/schema";

export default function MethodPage() {
  useEffect(() => {
    const title = "Method - Munthly";
    const description = "Discover the Munthly Method - a simple approach to budgeting that creates a stable monthly balance sheet. Learn how to organise your finances effectively.";
    
    document.title = title;
    setMetaDescription(description);
    const canonicalCleanup = setCanonicalUrl("/method");
    const ogCleanup = setOpenGraphTags(title, description, "/method", undefined, "article");
    const twitterCleanup = setTwitterCardTags(title, description);
    
    // Add schema for method page
    const webPageSchema = generateWebPageSchema(
      "/method",
      title,
      description
    );
    const articleSchema = generateArticleSchema(
      "/method",
      "The Munthly Method",
      "Simple budgeting that actually works. Learn how the Munthly Method helps you create a stable monthly balance sheet by organising your spending into regular expenses, provisions, and savings.",
      new Date().toISOString().split("T")[0]
    );
    
    const schemaCleanup = addSchemaToPage([webPageSchema, articleSchema]);
    
    return () => {
      canonicalCleanup();
      ogCleanup();
      twitterCleanup();
      schemaCleanup();
    };
  }, []);

  return (
    <TextPageLayout
      title="The Munthly Method"
      subtitle="Simple budgeting that actually works"
    >
      <div className="space-y-6 text-base leading-relaxed">
        <p className="text-lg">
          Managing money can feel overwhelming. That's why we built Munthly around one simple idea: turn everything into a monthly balance sheet.
        </p>

        <p>
          We all have our regular bills — mortgages, groceries, subscriptions. But it's those bigger, future expenses that catch us off guard. A holiday here, a car repair there, and suddenly your monthly spending looks completely different.
        </p>

        <p>
          The Munthly Method stops that yo-yo effect. By splitting future costs across the months leading up to them, you only spend money you actually have available. No surprises, no scrambling. Just a stable, predictable balance sheet.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">How we organise your spending</h2>
        
        <p>
          We break your outgoings into three simple categories:
        </p>

        <div className="space-y-4 mt-4">
          <div>
            <h3 className="font-semibold mb-2">Regular Expenses</h3>
            <p className="text-muted-foreground">
              The things that happen regularly — your mortgage, weekly food shop, TV subscriptions. These are the predictable costs that come around like clockwork.
            </p>
          </div>

          <div>
            <h3 className="font-semibold mb-2">Provisions</h3>
            <p className="text-muted-foreground">
              One-off payments in the future, like that holiday you're planning or a new car. Munthly automatically works out how much to set aside each month so you're ready when the time comes.
            </p>
          </div>

          <div>
            <h3 className="font-semibold mb-2">Savings</h3>
            <p className="text-muted-foreground">
              Similar to provisions, but with a different purpose. You can add a regular monthly saving amount for ongoing funds, or set a savings goal — like building an emergency fund — and we'll break it down into manageable monthly chunks.
            </p>
          </div>
        </div>

        <h2 className="text-xl font-semibold mt-8 mb-4">How it works</h2>

        <p>
          Start by adding your monthly income sources. Then log your expenses — whether they're weekly, monthly, or annual. Add provisions for future costs and set savings goals. Munthly does the maths for you, breaking everything down into monthly amounts so you can see exactly what you have available.
        </p>

        <p>
          The result? A clear picture of your finances that doesn't change dramatically from month to month. You'll know exactly where you stand, and you'll be prepared for what's coming.
        </p>

        <div className="bg-[var(--surface-container)] rounded-2xl p-6 mt-8">
          <h3 className="font-semibold mb-2">A note about banking</h3>
          <p className="text-sm text-muted-foreground">
            Munthly is a free budgeting tool and doesn't connect to your bank accounts. We recommend using modern banking apps like Monzo or Revolut to set up "pots" where you can physically separate and track your provisions and savings. This way, your Munthly budget matches what's actually in your accounts.
          </p>
        </div>
      </div>
    </TextPageLayout>
  );
}
